import React, { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, Button, FormControl, IconButton, TextField } from '@mui/material'
import { GridRowModel, GridToolbarContainer } from '@mui/x-data-grid'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useQuery } from '@tanstack/react-query'
import BillAPI from 'api/orbitV1/BillAPI'
import CustomSearchbar from 'common/AbstractDataGrid/CustomSearchbar'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import PageLayout from 'common/Layout/Page/PageLayout'
import TableSkeleton from 'common/Skeletons/TableSkeleton'
import { ColorBoxContent } from 'common/ui/colorBox'
import { PdfDownloadIcon } from 'common/ui/icons'
import { PAYMENT_METHODS } from 'constants/payment'
import JoyrideContext from 'context/joyride/joyrideContext'
import { getDate } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'
import { PaymentModal } from 'pages/Payment/components'
import { IBills } from 'types/bills'

/**
 * Shows the bills incurred by the customer, and allows the customer to download and pay for the bills
 * @returns {React.ReactNode}
 */
export default function BillingTable() {
  const joyrideContext = useContext(JoyrideContext)
  const { setRun, tourActive, setStepIndex } = joyrideContext

  const [bills, setBills] = useState<IBills[]>([])
  const [billPrice, setBillPrice] = useState(0)
  const [billingMetaInfo, setBillingMetaInfo] = useState({})

  const email = OrbitSession.get('email')
  const orgId = OrbitSession.get('orgId')

  /* Payment variables */
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState(
    PAYMENT_METHODS.BANK_TRANSFER
  )

  /* Billing vars */
  const ORDER_LIMIT = 150

  const MIN_DATE = new Date('2016-01-01')
  const MAX_DATE = new Date()
  // set start date to be three months before current
  const defaultStartDate = new Date()
  defaultStartDate.setDate(1)
  defaultStartDate.setMonth(new Date().getMonth() - 3)

  // set end date to be current month
  const defaultEndDate = new Date()
  defaultEndDate.setMonth(defaultEndDate.getMonth())
  defaultEndDate.setDate(1)

  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate)
  const [endDate, setEndDate] = useState<Date | null>(defaultEndDate)

  /* useQuery to retrieve bills */
  const {
    data: retrievedAllBills,
    error: retrievedAllBillsError,
    isLoading,
    refetch: refetchBills
  } = useQuery(
    ['getAllBillsWithinPeriod', startDate, endDate, orgId],
    async () => {
      const { data } = await BillAPI.getOrgBills(
        orgId,
        startDate,
        endDate,
        ORDER_LIMIT
      )
      return data
    }
  )

  if (retrievedAllBillsError) {
    toast.error('Error retrieving bills. Please try again later!')
  }

  useEffect(() => {
    if (retrievedAllBills) {
      setBills(retrievedAllBills)
      if (tourActive) {
        setTimeout(() => {
          setRun(true)
          setStepIndex(12)
        }, 500)
      }
    }
  }, [retrievedAllBills])

  /* Data grid components */
  const rows: GridRowModel[] = bills.map((bill, index) => {
    return {
      id: bill._id,
      index,
      invoiceDate: getDate(bill.billingDate),
      invoiceNo: bill.billNo ? bill.billNo : '-',
      billNo: bill.billNo,
      billingPeriod: bill.billPeriod,
      amount: bill.amount,
      status: bill.status,
      _raw: bill
    }
  })

  const columns = [
    { field: 'invoiceDate', headerName: 'Invoice Date', width: 100 },
    { field: 'invoiceNo', headerName: 'Invoice No.', width: 100 },
    { field: 'billingPeriod', headerName: 'Billing Period', width: 50 },
    { field: 'amount', headerName: 'Amount', width: 50 },
    { field: 'status', headerName: 'Status', width: 50 },
    {
      field: 'PDF',
      headerName: 'Download',
      width: 20,
      renderCell: (params) => {
        const bill = params.row
        return (
          <IconButton onClick={() => getBillPdf(bill)} data-cy='pdfDownload'>
            <PdfDownloadIcon />
          </IconButton>
        )
      }
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 50,
      renderCell: (params) => {
        const bill = params.row
        return (
          <Button
            disabled={bill.status === 'PAID'}
            variant='contained'
            onClick={() => {
              payBill(bill)
            }}
            data-cy='payBillBtn'
          >
            Pay
          </Button>
        )
      }
    }
  ]

  const AccountToolbar = () => {
    return (
      <GridToolbarContainer
        sx={{
          justifyContent: 'space-between'
        }}
      >
        <CustomSearchbar />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <FormControl variant='standard' sx={{ m: 1, minWidth: 120 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  views={['year', 'month']}
                  label='Start Date'
                  value={startDate}
                  minDate={MIN_DATE}
                  maxDate={MAX_DATE}
                  onChange={(newValue) => {
                    setStartDate(newValue)
                  }}
                  renderInput={(params) => (
                    <TextField {...params} helperText={null} />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
            <FormControl variant='standard' sx={{ m: 1, minWidth: 120 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  views={['year', 'month']}
                  label='End Date'
                  value={endDate}
                  minDate={MIN_DATE}
                  maxDate={MAX_DATE}
                  onChange={(newValue) => {
                    setEndDate(newValue)
                  }}
                  renderInput={(params) => (
                    <TextField {...params} helperText={null} />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Box>
        </Box>
      </GridToolbarContainer>
    )
  }

  const getBillPdf = async (selectedBill) => {
    const billPromise = BillAPI.getBillPdf(selectedBill.billNo)
    toast.promise(billPromise, {
      pending: 'Retrieving your bill, please wait...',
      success: `Successfully downloaded bill ${selectedBill.billNo}`,
      error: 'Error downloading Bill. Please try again later!'
    })
  }

  const payBill = async (selectedBill) => {
    setBillPrice(selectedBill.amount)
    setBillingMetaInfo({
      data: {
        billId: selectedBill.id,
        billNo: selectedBill.billNo,
        itemName: `Bill ${selectedBill.billNo}`,
        orgId,
        email
      }
    })
    setDialogOpen(true)
  }

  return (
    <PageLayout header='Bills'>
      <ColorBoxContent>
        <Box>
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <div id='bills_billsDataGrid'>
              <MUIDataGrid
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'invoiceDate', sort: 'desc' }]
                  }
                }}
                rows={rows}
                columns={columns}
                components={{ Toolbar: AccountToolbar }}
              />
            </div>
          )}
        </Box>
      </ColorBoxContent>
      <PaymentModal
        setDialogOpen={setDialogOpen}
        isDialogOpen={isDialogOpen}
        amountPayable={billPrice} // pass amount to pay into modal
        orgId={orgId}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        PAYMENT_METHODS={PAYMENT_METHODS}
        metaInfo={billingMetaInfo}
        email={email}
        handleNext={refetchBills}
        variant='bill'
      />
    </PageLayout>
  )
}
